package fr.labodoc.healthProfessional.webApp.data.repository

import arrow.core.Either
import fr.labodoc.healthProfessional.webApp.data.error.RepositoryError
import fr.labodoc.healthProfessional.webApp.data.model.HealthDirectoryHealthProfessionalModel
import fr.labodoc.healthProfessional.webApp.data.model.HealthProfessionalUserModel
import fr.labodoc.healthProfessional.webApp.data.source.remote.UserRemoteDataSource
import fr.labodoc.domain.labodoc.InputFile
import fr.labodoc.domain.labodoc.common.NotificationFrequency

class UsersRepositoryImpl(
  private val userRemoteDataSource: UserRemoteDataSource
) : UsersRepository {
  override suspend fun login(
    emailAddress: String,
    password: String,
    rememberMe: Boolean
  ): Either<RepositoryError, HealthProfessionalUserModel> =
    userRemoteDataSource
      .login(
        emailAddress = emailAddress,
        password = password,
        rememberMe = rememberMe
      )
      .mapLeft(RepositoryError::DataSource)

  override suspend fun logout(
  ): Either<RepositoryError, Unit> =
    userRemoteDataSource
      .logout()
      .mapLeft(RepositoryError::DataSource)

  override suspend fun deleteAccount(
  ): Either<RepositoryError, Unit> =
    userRemoteDataSource
      .deleteAccount()
      .mapLeft(RepositoryError::DataSource)

  override suspend fun sendPasswordResetRequest(
    emailAddress: String
  ): Either<RepositoryError, Unit> =
    userRemoteDataSource
      .sendPasswordResetRequest(
        emailAddress = emailAddress
      )
      .mapLeft(RepositoryError::DataSource)

  override suspend fun finalizePasswordResetRequest(
    token: String,
    password: String
  ): Either<RepositoryError, Unit> =
    userRemoteDataSource
      .finalizePasswordResetRequest(
        token = token,
        password = password
      )
      .mapLeft(RepositoryError::DataSource)

  override suspend fun changePassword(
    previousPassword: String,
    newPassword: String
  ): Either<RepositoryError, Unit> =
    userRemoteDataSource
      .changePassword(
        previousPassword = previousPassword,
        newPassword = newPassword
      )
      .mapLeft(RepositoryError::DataSource)

  override suspend fun getSelf(
  ): Either<RepositoryError, HealthProfessionalUserModel> =
    userRemoteDataSource
      .getSelf()
      .mapLeft(RepositoryError::DataSource)

  override suspend fun registerFcmRegistrationToken(
    token: String
  ): Either<RepositoryError, Unit> =
    userRemoteDataSource
      .registerFcmRegistrationToken(
        token = token
      )
      .mapLeft(RepositoryError::DataSource)

  override suspend fun deleteFcmRegistrationToken(
    token: String
  ): Either<RepositoryError, Unit> =
    userRemoteDataSource
      .deleteFcmRegistrationToken(
        token = token
      )
      .mapLeft(RepositoryError::DataSource)

  override suspend fun updateMedicalInterests(
    medicalInterestIds: List<String>
  ): Either<RepositoryError, Unit> =
    userRemoteDataSource
      .updateMedicalInterests(
        medicalInterestIds = medicalInterestIds
      )
      .mapLeft(RepositoryError::DataSource)

  override suspend fun updateNotificationsSettings(
    notificationFrequency: NotificationFrequency?
  ): Either<RepositoryError, Unit> =
    userRemoteDataSource
      .updateNotificationsSettings(
        notificationFrequency = notificationFrequency
      )
      .mapLeft(RepositoryError::DataSource)

  override suspend fun getHealthProfessionalByCardNumber(
    medicalCardNumber: String
  ): Either<RepositoryError, HealthDirectoryHealthProfessionalModel> =
    userRemoteDataSource
      .getHealthProfessionalByCardNumber(
        medicalCardNumber = medicalCardNumber
      )
      .mapLeft(RepositoryError::DataSource)

  override suspend fun registerWithMedicalCardCPS(
    medicalCardNumber: String,
    firstName: String,
    lastName: String,
    healthDirectoryProfessionCode: String,
    healthDirectoryProfessionalCategoryCode: String,
    healthDirectoryProfessionalStatusCode: String,
    healthDirectoryExpertiseCode: String?,
    healthDirectoryPharmacistSectionCode: String?,
    labodocMedicalProfessionId: String,
    labodocMedicalSpecialityId: String,
    labodocMedicalInterestIds: List<String>,
    departmentCode: String,
    notificationFrequency: NotificationFrequency?,
    emailAddress: String,
    password: String,
    partnerCode: String?
  ): Either<RepositoryError, Unit> =
    userRemoteDataSource
      .registerWithMedicalCardCPS(
        medicalCardNumber = medicalCardNumber,
        firstName = firstName,
        lastName = lastName,
        healthDirectoryProfessionCode = healthDirectoryProfessionCode,
        healthDirectoryProfessionalCategoryCode = healthDirectoryProfessionalCategoryCode,
        healthDirectoryProfessionalStatusCode = healthDirectoryProfessionalStatusCode,
        healthDirectoryExpertiseCode = healthDirectoryExpertiseCode,
        healthDirectoryPharmacistSectionCode = healthDirectoryPharmacistSectionCode,
        labodocMedicalProfessionId = labodocMedicalProfessionId,
        labodocMedicalSpecialityId = labodocMedicalSpecialityId,
        labodocMedicalInterestIds = labodocMedicalInterestIds,
        departmentCode = departmentCode,
        notificationFrequency = notificationFrequency,
        emailAddress = emailAddress,
        password = password,
        partnerCode = partnerCode
      )
      .mapLeft(RepositoryError::DataSource)

  override suspend fun registerWithMedicalCardCPF(
    rppsNumber: String?,
    medicalCardNumber: String,
    firstName: String,
    lastName: String,
    healthDirectoryProfessionCode: String,
    medicalDiplomaId: String,
    medicalInterestIds: List<String>,
    universityHospitalId: String,
    notificationFrequency: NotificationFrequency?,
    emailAddress: String,
    password: String,
    partnerCode: String?
  ): Either<RepositoryError, Unit> =
    userRemoteDataSource
      .registerWithMedicalCardCPF(
        rppsNumber = rppsNumber,
        medicalCardNumber = medicalCardNumber,
        firstName = firstName,
        lastName = lastName,
        healthDirectoryProfessionCode = healthDirectoryProfessionCode,
        medicalDiplomaId = medicalDiplomaId,
        medicalInterestIds = medicalInterestIds,
        universityHospitalId = universityHospitalId,
        notificationFrequency = notificationFrequency,
        emailAddress = emailAddress,
        password = password,
        partnerCode = partnerCode
      )
      .mapLeft(RepositoryError::DataSource)

  override suspend fun registerWithoutMedicalCardHealthProfessional(
    rppsNumber: String?,
    firstName: String,
    lastName: String,
    phoneNumber: String,
    medicalSpecialityId: String,
    medicalInterestIds: List<String>,
    facilityName: String,
    facilityUnit: String,
    facilityCity: String,
    facilityDepartmentCode: String,
    supportingDocument: InputFile,
    notificationFrequency: NotificationFrequency?,
    emailAddress: String,
    password: String,
    comment: String?,
    partnerCode: String?
  ): Either<RepositoryError, Unit> =
    userRemoteDataSource
      .registerWithoutMedicalCardHealthProfessional(
        rppsNumber = rppsNumber,
        firstName = firstName,
        lastName = lastName,
        phoneNumber = phoneNumber,
        medicalSpecialityId = medicalSpecialityId,
        medicalInterestIds = medicalInterestIds,
        facilityName = facilityName,
        facilityUnit = facilityUnit,
        facilityCity = facilityCity,
        facilityDepartmentCode = facilityDepartmentCode,
        supportingDocument = supportingDocument,
        notificationFrequency = notificationFrequency,
        emailAddress = emailAddress,
        password = password,
        comment = comment,
        partnerCode = partnerCode
      )
      .mapLeft(RepositoryError::DataSource)

  override suspend fun registerWithoutMedicalCardExtern(
    firstName: String,
    lastName: String,
    universityHospitalId: String,
    supportingDocument: InputFile,
    notificationFrequency: NotificationFrequency?,
    emailAddress: String,
    password: String,
    comment: String?,
    partnerCode: String?
  ): Either<RepositoryError, Unit> =
    userRemoteDataSource
      .registerWithoutMedicalCardExtern(
        firstName = firstName,
        lastName = lastName,
        universityHospitalId = universityHospitalId,
        supportingDocument = supportingDocument,
        notificationFrequency = notificationFrequency,
        emailAddress = emailAddress,
        password = password,
        comment = comment,
        partnerCode = partnerCode
      )
      .mapLeft(RepositoryError::DataSource)

  override suspend fun confirmEmail(
    token: String
  ): Either<RepositoryError, Unit> =
    userRemoteDataSource
      .confirmEmail(
        token = token
      )
      .mapLeft(RepositoryError::DataSource)
}
